/**
 * 🎯 Skill 管理頁面
 * 用於可視化管理包容的所有技能
 */

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './SkillManager.css';

interface SkillInfo {
  id: string;
  name: string;
  category: 'shared' | 'core' | 'soul' | 'special';
  path: string;
  content: string;
  isCore: boolean;
  lastModified?: string;
}

interface SkillCategory {
  id: string;
  name: string;
  description: string;
  skills: SkillInfo[];
}

export default function SkillManager() {
  const [categories, setCategories] = useState<SkillCategory[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<SkillInfo | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewSkillModal, setShowNewSkillModal] = useState(false);
  const [newSkill, setNewSkill] = useState({
    category: 'soul' as 'core' | 'soul' | 'special',
    name: '',
    content: '# 技能名稱\n\n## 描述\n\n## 使用時機\n\n## 範例\n'
  });

  // 取得所有 Skill
  const fetchSkills = useCallback(async () => {
    try {
      const res = await apiFetch('/api/skills');
      if (!res.ok) {
        throw new Error('無法載入 Skill 列表');
      }
      const data = await res.json();
      setCategories(data.categories || []);
      setError(null);
    } catch (err) {
      console.error('Fetch skills error:', err);
      setError('載入 Skill 失敗');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  // 選擇 Skill
  const handleSelectSkill = (skill: SkillInfo) => {
    if (isEditing && editContent !== selectedSkill?.content) {
      if (!confirm('有未儲存的變更，確定要切換嗎？')) {
        return;
      }
    }
    setSelectedSkill(skill);
    setEditContent(skill.content);
    setIsEditing(false);
  };

  // 儲存 Skill
  const handleSave = async () => {
    if (!selectedSkill) return;

    setIsSaving(true);
    try {
      const res = await apiFetch(`/api/skills/${encodeURIComponent(selectedSkill.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent })
      });

      if (!res.ok) throw new Error('儲存失敗');

      const data = await res.json();
      if (data.success) {
        setSelectedSkill({ ...selectedSkill, content: editContent });
        setIsEditing(false);
        await fetchSkills(); // 重新載入列表
        alert('儲存成功！');
      }
    } catch (err) {
      console.error('Save skill error:', err);
      alert('儲存失敗');
    } finally {
      setIsSaving(false);
    }
  };

  // 新增 Skill
  const handleCreateSkill = async () => {
    if (!newSkill.name || !newSkill.content) {
      alert('請填寫名稱和內容');
      return;
    }

    try {
      const res = await apiFetch('/api/skills', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSkill)
      });

      if (!res.ok) throw new Error('建立失敗');

      const data = await res.json();
      if (data.success) {
        setShowNewSkillModal(false);
        setNewSkill({
          category: 'soul',
          name: '',
          content: '# 技能名稱\n\n## 描述\n\n## 使用時機\n\n## 範例\n'
        });
        await fetchSkills();
        alert('Skill 建立成功！');
      } else {
        alert(data.error || '建立失敗');
      }
    } catch (err) {
      console.error('Create skill error:', err);
      alert('建立失敗');
    }
  };

  // 刪除 Skill
  const handleDelete = async () => {
    if (!selectedSkill) return;

    if (selectedSkill.isCore || selectedSkill.category === 'shared') {
      alert('無法刪除核心 Skill');
      return;
    }

    if (!confirm(`確定要刪除 "${selectedSkill.name}" 嗎？此操作無法復原。`)) {
      return;
    }

    try {
      const res = await apiFetch(`/api/skills/${encodeURIComponent(selectedSkill.id)}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('刪除失敗');

      const data = await res.json();
      if (data.success) {
        setSelectedSkill(null);
        setEditContent('');
        await fetchSkills();
        alert('Skill 已刪除');
      } else {
        alert(data.error || '刪除失敗');
      }
    } catch (err) {
      console.error('Delete skill error:', err);
      alert('刪除失敗');
    }
  };

  // 返回 Dashboard
  const handleBack = () => {
    window.location.href = '/admin';
  };

  const getCategoryIcon = (id: string) => {
    switch (id) {
      case 'shared': return '🏠';
      case 'core': return '🧠';
      case 'soul': return '💜';
      case 'special': return '✨';
      default: return '📁';
    }
  };

  const getCategoryColor = (id: string) => {
    switch (id) {
      case 'shared': return '#ff6b9d';
      case 'core': return '#4ecdc4';
      case 'soul': return '#a855f7';
      case 'special': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  if (isLoading) {
    return (
      <div className="skill-manager">
        <div className="sm-loading">
          <div className="sm-loading-icon">🎯</div>
          <div>載入 Skill 中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="skill-manager">
      {/* Header */}
      <header className="sm-header">
        <div className="sm-header-left">
          <button className="sm-back-btn" onClick={handleBack}>
            ← 返回監控台
          </button>
          <h1 className="sm-title">🎯 Skill 管理</h1>
        </div>
        <div className="sm-header-actions">
          <button className="sm-btn sm-btn-primary" onClick={() => setShowNewSkillModal(true)}>
            + 新增 Skill
          </button>
        </div>
      </header>

      {error && (
        <div className="sm-error-banner">
          ⚠️ {error}
          <button onClick={fetchSkills}>重試</button>
        </div>
      )}

      <div className="sm-container">
        {/* Left: Skill List */}
        <aside className="sm-sidebar">
          {categories.map(cat => (
            <div key={cat.id} className="sm-category">
              <div
                className="sm-category-header"
                style={{ borderLeftColor: getCategoryColor(cat.id) }}
              >
                <span className="sm-category-icon">{getCategoryIcon(cat.id)}</span>
                <div className="sm-category-info">
                  <div className="sm-category-name">{cat.name}</div>
                  <div className="sm-category-desc">{cat.description}</div>
                </div>
                <span className="sm-category-count">{cat.skills.length}</span>
              </div>
              <div className="sm-skill-list">
                {cat.skills.map(skill => (
                  <div
                    key={skill.id}
                    className={`sm-skill-item ${selectedSkill?.id === skill.id ? 'active' : ''} ${skill.isCore ? 'core' : ''}`}
                    onClick={() => handleSelectSkill(skill)}
                  >
                    <span className="sm-skill-name">{skill.name}</span>
                    {skill.isCore && <span className="sm-core-badge">核心</span>}
                  </div>
                ))}
                {cat.skills.length === 0 && (
                  <div className="sm-skill-empty">尚無技能</div>
                )}
              </div>
            </div>
          ))}
        </aside>

        {/* Right: Editor */}
        <main className="sm-main">
          {selectedSkill ? (
            <>
              <div className="sm-editor-header">
                <div className="sm-editor-title">
                  <h2>{selectedSkill.name}</h2>
                  <span className="sm-editor-path">{selectedSkill.path}</span>
                </div>
                <div className="sm-editor-actions">
                  {isEditing ? (
                    <>
                      <button
                        className="sm-btn sm-btn-secondary"
                        onClick={() => {
                          setEditContent(selectedSkill.content);
                          setIsEditing(false);
                        }}
                      >
                        取消
                      </button>
                      <button
                        className="sm-btn sm-btn-primary"
                        onClick={handleSave}
                        disabled={isSaving}
                      >
                        {isSaving ? '儲存中...' : '儲存'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="sm-btn sm-btn-secondary"
                        onClick={() => setIsEditing(true)}
                      >
                        編輯
                      </button>
                      {!selectedSkill.isCore && selectedSkill.category !== 'shared' && (
                        <button
                          className="sm-btn sm-btn-danger"
                          onClick={handleDelete}
                        >
                          刪除
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="sm-editor-meta">
                <span>類別: {selectedSkill.category}</span>
                {selectedSkill.lastModified && (
                  <span>最後修改: {new Date(selectedSkill.lastModified).toLocaleString('zh-TW')}</span>
                )}
              </div>

              <div className="sm-editor-content">
                {isEditing ? (
                  <textarea
                    className="sm-editor-textarea"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    placeholder="輸入 Skill 內容（Markdown 格式）..."
                  />
                ) : (
                  <div className="sm-editor-preview">
                    <pre>{selectedSkill.content}</pre>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="sm-empty-state">
              <div className="sm-empty-icon">🎯</div>
              <h3>選擇一個 Skill 開始編輯</h3>
              <p>從左側列表選擇要查看或編輯的技能</p>
            </div>
          )}
        </main>
      </div>

      {/* New Skill Modal */}
      {showNewSkillModal && (
        <div className="sm-modal-overlay" onClick={() => setShowNewSkillModal(false)}>
          <div className="sm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sm-modal-header">
              <h3>新增 Skill</h3>
              <button className="sm-modal-close" onClick={() => setShowNewSkillModal(false)}>
                ×
              </button>
            </div>
            <div className="sm-modal-body">
              <div className="sm-form-group">
                <label>類別</label>
                <select
                  value={newSkill.category}
                  onChange={(e) => setNewSkill({ ...newSkill, category: e.target.value as any })}
                >
                  <option value="core">核心技能 (core)</option>
                  <option value="soul">靈魂技能 (soul)</option>
                  <option value="special">特殊技能 (special)</option>
                </select>
              </div>
              <div className="sm-form-group">
                <label>目錄名稱</label>
                <input
                  type="text"
                  placeholder="例如: my_skill（只能用小寫英文、數字、底線）"
                  value={newSkill.name}
                  onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
                />
              </div>
              <div className="sm-form-group">
                <label>內容（SKILL.md）</label>
                <textarea
                  placeholder="輸入 Skill 內容..."
                  value={newSkill.content}
                  onChange={(e) => setNewSkill({ ...newSkill, content: e.target.value })}
                  rows={10}
                />
              </div>
            </div>
            <div className="sm-modal-footer">
              <button
                className="sm-btn sm-btn-secondary"
                onClick={() => setShowNewSkillModal(false)}
              >
                取消
              </button>
              <button
                className="sm-btn sm-btn-primary"
                onClick={handleCreateSkill}
              >
                建立
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
